import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import GameHeader from '../GameHeader';
import StatusRow from '../StatusRow';
import EditPanel from './EditPanel';
import ManagePanel from './ManagePanel';
import { taskPhase } from './ttUtils';
import '../games.css';
import './teamtask.css';

const GAME_STATUSES = [
    { value: 'DEVELOP', label: 'Подготовка', tone: 'dev' },
    { value: 'ACTIVE', label: 'Активна', tone: 'active' },
    { value: 'FINISHED', label: 'Завершена', tone: 'done' }
];

// Игра «Командные задания»: head-card (инфо + статус) + табы редактирование/управление.
export default function TeamTaskGame({ shift }) {
    const smenaId = shift.id;
    const { data: game, isLoading, error } = useApiQuery(
        ['teamtask', 'game', smenaId],
        `/api/team-tasks/admin/game?smenaId=${smenaId}`,
        { enabled: !!smenaId, refetchOnMount: 'always', retry: false }
    );

    if (error) {
        const msg = error.response?.status === 404
            ? 'Игра «Командные задания» не заведена на смену — активируйте её в разделе «Смена»'
            : (error.response?.data?.message || 'Ошибка загрузки игры «Командные задания»');
        return <div className="vp-empty">{msg}</div>;
    }
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!game) return <div className="vp-empty">Игра «Командные задания» не найдена для смены</div>;

    return <TeamTaskContent game={game} shift={shift} />;
}

TeamTaskGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function TeamTaskContent({ game, shift }) {
    const smenaId = shift.id;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [tab, setTab] = useState('edit');
    const [status, setStatus] = useState(game.status);

    const { data: tasks = [], refetch: refetchTasks } = useApiQuery(
        ['teamtask', 'tasks', smenaId],
        `/api/team-tasks/admin/list-all?smenaId=${smenaId}`,
        { refetchOnMount: 'always' }
    );
    const { data: pending = [] } = useApiQuery(
        ['teamtask', 'submissions', smenaId, 'PENDING'],
        '/api/team-tasks/admin/submissions?status=PENDING',
        { refetchOnMount: 'always' }
    );

    const changeStatus = async (newStatus) => {
        show();
        try {
            await apiClient.post(`/api/team-tasks/admin/game/status?smenaId=${smenaId}&status=${newStatus}`);
            setStatus(newStatus);
            enqueueSnackbar('Статус обновлён', { variant: 'success' });
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при обновлении статуса', { variant: 'error' }); }
        finally { hide(); }
    };

    const open = tasks.filter(t => taskPhase(t).tone === 'open').length;
    const meta = (
        <>
            <span><b>{tasks.length}</b> заданий</span>
            <span className="dot">·</span>
            <span><b>{open}</b> идёт приём</span>
            <span className="dot">·</span>
            <span><b>{pending.length}</b> на проверке</span>
        </>
    );

    return (
        <>
            <GameHeader
                title={`Командные задания — ${shift.title || ''}`}
                meta={meta}
                status={<StatusRow statuses={GAME_STATUSES} current={status} onChange={changeStatus} />}
            />
            <section className="content white">
                <div className="bt-tabs">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a className={tab === 'edit' ? 'on' : ''} onClick={() => setTab('edit')}>Редактирование</a>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a className={tab === 'manage' ? 'on' : ''} onClick={() => setTab('manage')}>
                        Управление
                        {pending.length > 0 && <span className="tt-badge">{pending.length}</span>}
                    </a>
                </div>
                {tab === 'edit' && <EditPanel smenaId={smenaId} gameStatus={status} tasks={tasks} onChanged={refetchTasks} />}
                {tab === 'manage' && <ManagePanel smenaId={smenaId} tasks={tasks} />}
            </section>
        </>
    );
}

TeamTaskContent.propTypes = {
    game: PropTypes.object.isRequired,
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};
